import { useLayoutEffect, useState } from 'react';
import {
  measureSearchBarBottom,
  SEARCH_BAR_CONTAINER_SELECTOR,
  subscribeViewport
} from './helpers';

export type ParallaxStickyPin = {
  isPinned: boolean;
  top: number;
  left: number;
  width: number;
  height: number;
};

const idlePin = (height: number, top = 0): ParallaxStickyPin => ({
  isPinned: false,
  top,
  left: 0,
  width: 0,
  height
});

export const useParallaxStickyPin = (
  slot: HTMLElement | null,
  enabled: boolean,
  stickyVisible: number
): ParallaxStickyPin => {
  const [pin, setPin] = useState<ParallaxStickyPin>(idlePin(stickyVisible));

  useLayoutEffect(() => {
    if (!enabled) {
      setPin(prev => (prev.isPinned ? idlePin(stickyVisible) : prev));
      return;
    }

    const update = () => {
      const pinTop = measureSearchBarBottom();
      if (!slot) {
        setPin(prev => (prev.isPinned ? idlePin(stickyVisible, pinTop) : prev));
        return;
      }

      const rect = slot.getBoundingClientRect();
      const isPinned = rect.bottom <= pinTop + stickyVisible;

      setPin(prev => {
        if (!isPinned) {
          if (
            !prev.isPinned &&
            prev.top === pinTop &&
            prev.height === stickyVisible
          ) {
            return prev;
          }
          return idlePin(stickyVisible, pinTop);
        }

        const next: ParallaxStickyPin = {
          isPinned: true,
          top: pinTop,
          left: Math.round(rect.left),
          width: Math.round(rect.width),
          height: stickyVisible
        };

        return prev.isPinned &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.height === next.height
          ? prev
          : next;
      });
    };

    const unsubscribe = subscribeViewport(update);
    const bar = document.querySelector(SEARCH_BAR_CONTAINER_SELECTOR);
    const ro =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (ro) {
      if (slot) ro.observe(slot);
      if (bar instanceof HTMLElement) ro.observe(bar);
    }

    return () => {
      unsubscribe();
      ro?.disconnect();
    };
  }, [enabled, stickyVisible, slot]);

  return pin;
};
